import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { Button, Popconfirm, Row, Space, Table, message } from 'antd'
import { type FilterValue } from 'antd/es/table/interface'
import { isPast } from 'date-fns'
import { useMemo, type FC } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiDeleteLicense } from '~/api/adminApi'
import { useTableDataSourceKey } from '~/hooks/useTableDataSourceKey'
import { useSoftwaresQuery } from '~/pages/query'
import { type License } from '~/types/license'
import { AWS_REGIONS, regionCodeToName } from '~/utils/constants'
import { clipboard, compareDate, toDateString } from '~/utils/helpers'
import { useLicensesQuery } from './query'

interface LicenseListProps {
  onEdit: (item: License) => void
  searchValue: string | null
  onFilter: (filters: any) => void
  softwareFilter: number[] | null
}

const LicenseList: FC<LicenseListProps> = ({
  searchValue,
  onEdit,
  onFilter,
  softwareFilter
}) => {
  const { data, isFetching } = useLicensesQuery()
  const source = useTableDataSourceKey(data?.data)
  const filtered = useMemo(() => {
    if (searchValue == null) {
      return source
    }
    return source.filter((item: License) =>
      `${item.name} ${item.license_key}`
        .toLowerCase()
        .includes(searchValue.toLowerCase())
    )
  }, [searchValue, source])
  const { data: softwares } = useSoftwaresQuery()
  const deleteMutation = useMutation(apiDeleteLicense)
  const queryClient = useQueryClient()

  return (
    <Table
      size='small'
      tableLayout='fixed'
      dataSource={filtered}
      loading={isFetching}
      pagination={{ pageSize: 20 }}
      footer={() => (
        <Row justify='start'>
          <span>Total: {data?.data.length} licenses</span>
        </Row>
      )}
      onChange={(_, filters: Record<string, FilterValue | null>) => {
        onFilter(filters)
      }}
    >
      <Table.Column
        title='Software'
        dataIndex='software_id'
        render={(_, record: License) => record.software?.name}
        filters={softwares?.map((i) => ({ text: i.name, value: i.id }))}
        filteredValue={softwareFilter}
        onFilter={(value, record) => record.software_id === value}
      />
      <Table.Column title='Mac Address' dataIndex='name' />
      <Table.Column
        title='Location'
        dataIndex='region_code'
        render={regionCodeToName}
        filters={AWS_REGIONS.map((item) => ({
          text: item.name,
          value: item.code
        }))}
        filterMultiple={false}
        filterSearch={(input, record) => {
          return (
            typeof record.text === 'string' &&
            record.text.toLowerCase().includes(input)
          )
        }}
        onFilter={(value, record: License) => {
          if (value == null) {
            return false
          }

          return value === record.region_code
        }}
      />
      <Table.Column title='Availability Zone' dataIndex='availability_zone' />
      <Table.Column
        title='Is being used'
        dataIndex='is_used'
        render={(value: boolean) => (value ? 'Yes' : '')}
        filterMultiple={false}
        filters={[
          { text: 'Yes', value: true },
          { text: 'No', value: false }
        ]}
        onFilter={(value, record: License) => record.is_used === value}
      />

      <Table.Column
        title='Cloudformation Logical Name'
        dataIndex='cloudformation_logical_name'
      />
      <Table.Column
        title='License Key'
        dataIndex='license_key'
        render={(_, record: License) => {
          return (
            <div
              style={{
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden'
              }}
              onClick={() => {
                clipboard(record.license_key)
                message.info('Copied')
              }}
            >
              {record.license_key}
            </div>
          )
        }}
      />
      <Table.Column
        title='Created at'
        dataIndex='created_at'
        render={toDateString}
        sorter={(a: License, b: License) => {
          return compareDate(a.created_at, b.created_at)
        }}
      />
      <Table.Column
        title='Activated at'
        dataIndex='activated_at'
        render={toDateString}
        sorter={(a: License, b: License) => {
          return compareDate(a.activated_at, b.activated_at)
        }}
      />
      <Table.Column
        title='Expired at'
        dataIndex='expired_at'
        render={(value) => (
          <span style={{ color: isPast(value) ? 'red' : '' }}>
            {toDateString(value)}
          </span>
        )}
        sorter={(a: License, b: License) => {
          return compareDate(a.expired_at, b.expired_at)
        }}
      />
      <Table.Column
        title='Actions'
        align='center'
        render={(_, record: License) => (
          <Space size='middle'>
            <Button
              key={1}
              type='primary'
              icon={<EditOutlined />}
              onClick={() => {
                onEdit(record)
              }}
            />
            <Popconfirm
              title={`Do you want to delete the license name ${record.name}?`}
              onConfirm={() => {
                deleteMutation.mutate(record.id, {
                  onSuccess: () => {
                    message.success('Deleted successfully')
                    void queryClient.invalidateQueries({ queryKey: ['licenses'] })
                  }
                })
              }}
              okText='YES'
            >
              <Button danger type='primary' icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        )}
      />
    </Table>
  )
}

export default LicenseList
